import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { KiwiMark } from "./KiwiMark";

const LINKS = [
  { label: "Home", hash: "#home", id: "nav-link-home" },
  { label: "About", hash: "#about", id: "nav-link-about" },
  { label: "Programs", hash: "#programs", id: "nav-link-programs" },
  { label: "Activities", hash: "#activities", id: "nav-link-activities" },
  { label: "Parent Feedback", hash: "#feedback", id: "nav-link-feedback" },
  { label: "Franchise", hash: "#franchise", id: "nav-link-franchise" },
  { label: "Contact", hash: "#contact", id: "nav-link-contact" },
];

const scrollToHash = (hash) => {
  const lenis = window.__lenis;
  if (lenis) lenis.scrollTo(hash, { offset: -80, duration: 1.4 });
  else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

export const Nav = ({ onEnquire }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) window.__lenis?.stop();
    else window.__lenis?.start();
  }, [open]);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollToHash(hash), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        data-testid="nav-bar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "glass shadow-[0_8px_30px_rgba(45,106,79,0.08)]" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-[72px] flex items-center justify-between">
          <a
            href="#home"
            data-testid="nav-link-home"
            onClick={(e) => go(e, "#home")}
            className="flex items-center gap-3 group"
          >
            <span className="transition-transform duration-500 group-hover:rotate-[25deg]">
              <KiwiMark className="w-10 h-10" />
            </span>
            <span className="leading-none">
              <span className="block font-play font-semibold text-lg text-ink">Kiwi Kids</span>
              <span className="block text-[10px] tracking-[0.28em] uppercase text-sage font-semibold mt-0.5">
                Pre-School
              </span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {LINKS.slice(1).map((l) => (
              <a
                key={l.hash}
                href={l.hash}
                data-testid={l.id}
                onClick={(e) => go(e, l.hash)}
                className="px-3.5 py-2 text-[13.5px] font-medium text-ink/70 hover:text-ink rounded-full hover:bg-ink/5 transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <motion.button
              data-testid="nav-cta-enquire-button"
              onClick={onEnquire}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.96 }}
              className="hidden sm:inline-flex items-center gap-1.5 bg-peach text-white font-semibold text-sm px-5 py-2.5 rounded-full shadow-[0_10px_24px_rgba(255,122,89,0.35)] hover:bg-ink transition-colors duration-300"
            >
              Enquire Now
              <ArrowUpRight className="w-4 h-4" />
            </motion.button>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              className="lg:hidden w-11 h-11 rounded-full glass flex items-center justify-center text-ink"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-cream/95 backdrop-blur-xl lg:hidden"
          >
            <div className="h-full flex flex-col justify-center px-8">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.hash}
                  href={l.hash}
                  data-testid={l.id}
                  onClick={(e) => go(e, l.hash)}
                  initial={{ opacity: 0, x: -32 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="font-display font-bold text-4xl py-3 text-ink border-b border-ink/8 last:border-0"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                onClick={() => {
                  setOpen(false);
                  setTimeout(onEnquire, 250);
                }}
                data-testid="nav-cta-enquire-button"
                className="mt-8 bg-peach text-white font-semibold px-6 py-4 rounded-full"
              >
                Enquire Now
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
